import * as React from 'react';
import {
  Box, Typography, Grid, TextField, Button, InputAdornment, FormControlLabel, Switch, Stack
} from '@mui/material';
import './Registration.css';
import india_flag from '../../assets/icons/india flag.png';
import { fetchOtp, registerFoodcourt, resendOtp, verifyCode, checkExistingUser, lookupIfsc, lookupGstin } from '../../services/api/registration/RegisterServices';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useLoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import jsPDF from 'jspdf';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

const steps = ['Establishment Details', 'Compliance Documentation', 'Partnership Agreement'];
const libraries: ('places')[] = ['places'];
const center = {
  lat: 17.4361932,
  lng: 78.3721234,
};

export default function CustomizedSteppers() {
  const [formData, setFormData] = React.useState({
    ownerId: "",
    foodCourtName: '',
    ownerName: '',
    ownerEmail: '',
    ownerMobile: '',
    address: '',
    city: '',
    latitude: String(center.lat),
    longitude: String(center.lng),
    panNumber: '',
    panName: '',
    gstin: '',
    fssaiNumber: '',
    fssaiExpiryDate: '',
    accountNumber: '',
    accountName: '',
    ifsc: '',
    bankBranch: '',
    status: 'open',
    menuQrCode: 'abcdefgh',
    imageUrl: 'abcdef',
    parentFoodCourtId: '',
    referralCode: '',
  });
  const [isOutlet, setIsOutlet] = React.useState(false);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
  const [markerPosition, setMarkerPosition] = React.useState(center);
  const [autocomplete, setAutocomplete] = React.useState<google.maps.places.Autocomplete | null>(null);
  const [stateValue, setStateValue] = React.useState<string>("");
  const [otpSent, setOtpSent] = React.useState(false);
  const [googleUser, setGoogleUser] = React.useState<{ name: string; email: string; picture?: string } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [authMobile, setAuthMobile] = React.useState('');
  const [authOtp, setAuthOtp] = React.useState('');
  const [existingLocations, setExistingLocations] = React.useState<Array<{ id: string; name: string; status: string }>>([]);
  const [validationErrors, setValidationErrors] = React.useState<Record<string, string>>({});
  const [draftRestored, setDraftRestored] = React.useState(false);

  // ── Validation helpers ──
  const validators: Record<string, { regex: RegExp; msg: string; okMsg: string }> = {
    panNumber: { regex: /^[A-Z]{5}[0-9]{4}[A-Z]$/, msg: 'Format: ABCDE1234F', okMsg: 'Valid PAN' },
    gstin: { regex: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/, msg: 'Format: 22AAAAA0000A1Z5', okMsg: 'Valid GSTIN' },
    fssaiNumber: { regex: /^[0-9]{14}$/, msg: 'Must be 14 digits', okMsg: 'Valid FSSAI' },
    ifsc: { regex: /^[A-Z]{4}0[A-Z0-9]{6}$/, msg: 'Format: SBIN0001234', okMsg: 'Valid IFSC' },
  };

  const validateField = (id: string, value: string) => {
    if (!validators[id]) return;
    if (!value) { setValidationErrors(p => { const n = { ...p }; delete n[id]; return n; }); return; }
    const v = validators[id];
    setValidationErrors(p => ({ ...p, [id]: v.regex.test(value) ? '' : v.msg }));
  };

  // ── Onboarding walkthrough slide ──
  const [activeSlide, setActiveSlide] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setActiveSlide(p => (p + 1) % 3), 3000);
    return () => clearInterval(t);
  }, []);

  // ── Session timeout (30 min) ──
  const [showTimeoutModal, setShowTimeoutModal] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimeout = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    setShowTimeoutModal(false);
    warningRef.current = setTimeout(() => setShowTimeoutModal(true), 25 * 60 * 1000);
    timeoutRef.current = setTimeout(() => {
      localStorage.removeItem('sermyble_reg_draft');
      setIsAuthenticated(false);
      toast.error('Session expired due to inactivity');
      setShowTimeoutModal(false);
    }, 30 * 60 * 1000);
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    resetTimeout();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimeout));
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimeout));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, resetTimeout]);

  // ── IFSC auto-lookup ──
  const initialLoadRef = React.useRef(true);
  React.useEffect(() => {
    if (initialLoadRef.current) return;
    if (formData.ifsc.length === 11 && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc)) {
      lookupIfsc(formData.ifsc).then(res => {
        if (res) {
          setFormData(prev => ({ ...prev, bankBranch: `${res.bank}, ${res.branch}` }));
          toast.success(`Bank: ${res.bank}, Branch: ${res.branch}`);
        }
      });
    }
  }, [formData.ifsc]);

  // ── GSTIN auto-verify ──
  React.useEffect(() => {
    if (initialLoadRef.current) return;
    if (formData.gstin.length === 15 && /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/.test(formData.gstin)) {
      lookupGstin(formData.gstin).then(res => {
        if (res && res.name) {
          setFormData(prev => ({ ...prev, panName: res.name }));
          toast.success(`Business: ${res.name}`);
        }
      });
    }
  }, [formData.gstin]);

  // ── Image compression ──
  const compressImage = (file: File): Promise<File> => {
    return new Promise(resolve => {
      if (!file.type.startsWith('image/') || file.size < 512000) { resolve(file); return; }
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 1200 / Math.max(img.width, img.height));
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (blob) {
            const compressed = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(compressed);
          } else resolve(file);
        }, 'image/jpeg', 0.8);
      };
      img.src = objectUrl;
    });
  };

  // ── Tooltip component ──
  const Tooltip = ({ text }: { text: string }) => (
    <span className="field-tooltip">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      <span className="tooltip-text">{text}</span>
    </span>
  );

  // ── FileDropZone component ──
  const FileDropZone = ({ id, label, file }: { id: string; label: string; file: File | null }) => {
    const [dragging, setDragging] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const handleDrop = async (e: React.DragEvent) => {
      e.preventDefault(); setDragging(false);
      const f = e.dataTransfer.files[0];
      if (f) { const compressed = await compressImage(f); setUploadedFiles(prev => ({ ...prev, [id]: compressed })); }
    };
    const preview = file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    return (
      <div
        className={`drop-zone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input ref={inputRef} type="file" hidden id={id} accept="image/*,.pdf" onChange={async e => {
          if (e.target.files?.[0]) {
            const compressed = await compressImage(e.target.files[0]);
            setUploadedFiles(prev => ({ ...prev, [id]: compressed }));
          }
        }} />
        {file ? (
          <div className="drop-zone-preview">
            {preview ? <img src={preview} alt="" className="drop-zone-thumb" /> : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            )}
            <div className="drop-zone-info">
              <span className="drop-zone-name">{file.name}</span>
              <span className="drop-zone-size">{(file.size / 1024).toFixed(0)} KB</span>
            </div>
            <button className="drop-zone-remove" onClick={e => { e.stopPropagation(); setUploadedFiles(prev => ({ ...prev, [id]: null })); }}>x</button>
          </div>
        ) : (
          <div className="drop-zone-empty">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span>{label}</span>
            <small>Drag & drop or click to browse</small>
          </div>
        )}
      </div>
    );
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      try {
        const decoded: any = jwtDecode(credentialResponse.credential);
        const name = decoded.name || '';
        const email = decoded.email || '';

        // Check if user already has food courts
        const checkResult = await checkExistingUser(email);
        if (checkResult.exists) {
          setExistingLocations(checkResult.existingLocations);
          toast.info(`Welcome back! You have ${checkResult.count} existing location(s). Registering a new one.`);
        }

        // If draft was from a different user, clear it
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.formData && parsed.formData.ownerEmail && parsed.formData.ownerEmail !== email) {
              localStorage.removeItem(STORAGE_KEY);
              setFormData(prev => ({ ...prev, ownerId: '', foodCourtName: '', ownerName: name, ownerEmail: email, ownerMobile: '', address: '', panNumber: '', panName: '', gstin: '', fssaiNumber: '', fssaiExpiryDate: '', accountNumber: '', accountName: '', ifsc: '', bankBranch: '', referralCode: '', parentFoodCourtId: '' }));
              setActiveStep(0);
              setUploadedFiles({ panCardImage: null, fssaiDocument: null, gstImage: null, bankDocumentFile: null });
            } else {
              setFormData(prev => ({ ...prev, ownerName: name, ownerEmail: email }));
            }
          } catch { setFormData(prev => ({ ...prev, ownerName: name, ownerEmail: email })); }
        } else {
          setFormData(prev => ({ ...prev, ownerName: name, ownerEmail: email }));
        }
        setGoogleUser({ name, email, picture: decoded.picture });
        setIsAuthenticated(true);
        if (!checkResult.exists) toast.success('Signed in with Google — fill in the rest to register!');
      } catch {
        toast.error('Failed to decode Google credentials');
      }
    }
  };

  const handleMapClick = React.useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newCoordinates = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      setMarkerPosition(newCoordinates);
      setFormData(prev => ({ ...prev, latitude: newCoordinates.lat.toString(), longitude: newCoordinates.lng.toString() }));
      getGeocodedAddress(newCoordinates.lat, newCoordinates.lng);
    }
  }, []);

  const handleMarkerDragEnd = React.useCallback((event: google.maps.MapMouseEvent) => {
    const newCoordinates = { lat: event.latLng!.lat(), lng: event.latLng!.lng() };
    setMarkerPosition(newCoordinates);
    setFormData(prev => ({ ...prev, latitude: newCoordinates.lat.toString(), longitude: newCoordinates.lng.toString() }));
    getGeocodedAddress(newCoordinates.lat, newCoordinates.lng);
  }, []);

  const getGeocodedAddress = async (lat: number, lng: number) => {
    try {
      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`);
      const data = await response.json();
      if (data.status === "OK" && data.results[0]) {
        setFormData((prev) => ({ ...prev, address: data.results[0].formatted_address }));
        // Extract city from geocoded result
        const components = data.results[0].address_components;
        if (components) {
          const cityComp = components.find((c: any) => c.types.includes('locality'));
          if (cityComp) setFormData(prev => ({ ...prev, city: cityComp.long_name }));
        }
      } else {
        toast.error("Failed to retrieve address");
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      toast.error("Error fetching address");
    }
  }

  const onLoadAutocomplete = (autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const newCoordinates = { lat: place.geometry.location?.lat()!, lng: place.geometry.location?.lng()! };
        const addressComponents = place.address_components;
        if (addressComponents) {
          const stateComponent = addressComponents.find(c => c.types.includes("administrative_area_level_1"));
          setStateValue(stateComponent ? stateComponent.long_name : '');
        }
        setMarkerPosition(newCoordinates);
        setFormData(prev => ({ ...prev, latitude: newCoordinates.lat.toString(), longitude: newCoordinates.lng.toString(), address: place.formatted_address || '' }));
        // Extract city from autocomplete result
        if (addressComponents) {
          const cityComp = addressComponents.find(c => c.types.includes('locality'));
          if (cityComp) setFormData(prev => ({ ...prev, city: cityComp.long_name }));
        }
      } else {
        toast.error("No details available for input: '" + place.name + "'");
      }
    }
  };

  const [activeStep, setActiveStep] = React.useState(0);

  // ── Auto-save to localStorage ──
  const STORAGE_KEY = 'sermyble_reg_draft';
  const [pendingDraft, setPendingDraft] = React.useState<any>(null);
  const [showDraftModal, setShowDraftModal] = React.useState(false);

  // On mount, just check if a draft exists (don't apply it yet)
  React.useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.formData && (parsed.formData.foodCourtName || parsed.formData.panNumber)) {
          setPendingDraft(parsed);
        }
      } catch { /* ignore */ }
    }
    setTimeout(() => { initialLoadRef.current = false; }, 500);
  }, []);

  // Show draft prompt after authentication if there's a pending draft
  React.useEffect(() => {
    if (isAuthenticated && pendingDraft) {
      setShowDraftModal(true);
    }
  }, [isAuthenticated, pendingDraft]);

  const handleRestoreDraft = () => {
    if (pendingDraft) {
      setFormData(prev => ({ ...prev, ...pendingDraft.formData }));
      if (pendingDraft.activeStep) setActiveStep(pendingDraft.activeStep);
      setDraftRestored(true);
    }
    setShowDraftModal(false);
    setPendingDraft(null);
  };

  const handleDiscardDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowDraftModal(false);
    setPendingDraft(null);
    setActiveStep(0);
  };

  React.useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ formData, activeStep }));
    }
  }, [formData, activeStep, isAuthenticated]);
  const [uploadedFiles, setUploadedFiles] = React.useState({
    panCardImage: null as File | null,
    gstImage: null as File | null,
    fssaiDocument: null as File | null,
    bankDocumentFile: null as File | null,
  });
  const [otp, setOtp] = React.useState('');
  const [isOtpVerified, setIsOtpVerified] = React.useState(false);
  const [isTimerActive, setIsTimerActive] = React.useState<boolean>(false);
  const [Loader, setLoader] = React.useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const autoUpper = ['panNumber', 'gstin', 'ifsc'].includes(id) ? value.toUpperCase() : value;
    setFormData(prev => ({ ...prev, [id]: autoUpper }));
    validateField(id, autoUpper);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, files } = e.target;
    if (files && files.length > 0) setUploadedFiles(prev => ({ ...prev, [id]: files[0] }));
  };

  const handleNext = () => {
    setActiveStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
  };

  const handleBack = () => setActiveStep((prev) => (prev > 0 ? prev - 1 : prev));

  const verifyOtp = async () => {
    if (otp.length === 6) {
      try {
        const res = await verifyCode({ mobileNumber: formData.ownerMobile, otpCode: otp });
        if (res) { toast.success("OTP Verified"); setIsOtpVerified(true); }
      } catch { toast.error("OTP Verification Failed"); setIsOtpVerified(false); }
    }
  };

  const verifyAuthOtp = async () => {
    if (authOtp.length === 6) {
      try {
        const res = await verifyCode({ mobileNumber: authMobile, otpCode: authOtp });
        if (res) {
          // Check if mobile already has food courts
          const checkResult = await checkExistingUser(undefined, authMobile);
          if (checkResult.exists) {
            setExistingLocations(checkResult.existingLocations);
            toast.info(`Welcome back! You have ${checkResult.count} existing location(s). Registering a new one.`);
          } else {
            toast.success('Mobile verified — let\'s register!');
          }
          // If draft was from a different user, clear it
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            try {
              const parsed = JSON.parse(saved);
              if (parsed.formData && parsed.formData.ownerMobile && parsed.formData.ownerMobile !== authMobile) {
                localStorage.removeItem(STORAGE_KEY);
                setFormData(prev => ({ ...prev, ownerId: '', foodCourtName: '', ownerName: '', ownerEmail: '', ownerMobile: authMobile, address: '', panNumber: '', panName: '', gstin: '', fssaiNumber: '', fssaiExpiryDate: '', accountNumber: '', accountName: '', ifsc: '', bankBranch: '', referralCode: '', parentFoodCourtId: '' }));
                setActiveStep(0);
                setUploadedFiles({ panCardImage: null, fssaiDocument: null, gstImage: null, bankDocumentFile: null });
              } else {
                setFormData(prev => ({ ...prev, ownerMobile: authMobile }));
              }
            } catch { setFormData(prev => ({ ...prev, ownerMobile: authMobile })); }
          } else {
            setFormData(prev => ({ ...prev, ownerMobile: authMobile }));
          }
          setIsOtpVerified(true);
          setIsAuthenticated(true);
        }
      } catch { toast.error('OTP Verification Failed'); }
    }
  };

  const sendAuthOtp = async () => {
    if (authMobile.length !== 10) { toast.warning('Enter a valid 10-digit mobile number'); return; }
    setOtpSent(true);
    try {
      const res = await fetchOtp({ mobileNumber: authMobile });
      if (res) toast.success('OTP sent to +91 ' + authMobile);
    } catch { toast.error('Failed to send OTP'); }
  };

  const sendOtp = async () => {
    if (formData.ownerMobile === "") { toast.warning("Please enter mobile number"); return; }
    setOtpSent(true);
    if (!isTimerActive) {
      setIsTimerActive(true);
      try {
        const res = await resendOtp(formData.ownerMobile);
        if (res) {
          toast.success("OTP Sent");
          let countdown = 30;
          const interval = setInterval(() => {
            if (countdown > 0) countdown--;
            else { clearInterval(interval); setIsTimerActive(false); }
          }, 1000);
        }
      } catch { toast.error("Failed to Send OTP"); }
    }
  };

  const [isChecked, setIsChecked] = React.useState(false);
  const handleCheckboxChange = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setIsChecked(event.target.checked);
  };

  const validateGSTNumber = (gstNumber: string) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}[Z]{1}[A-Z0-9]{1}$/.test(gstNumber);
  const validateFSSAINumber = (fssaiCode: string) => /^[0-9]{14}$/.test(fssaiCode);

  const generateAndDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(10);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxLineWidth = pageWidth - (margin * 2);
    doc.text("Partnership Agreement", margin, 10);
    doc.text(`Effective Date: ${new Date().toLocaleDateString()}`, margin, 20);
    let yPos = 30;
    const introText = `This Partnership Agreement is entered into by Sermyble and ${formData.foodCourtName}, represented by ${formData.ownerName}.`;
    let introLines = doc.splitTextToSize(introText, maxLineWidth);
    doc.text(introLines, margin, yPos);
    yPos += introLines.length * 6 + 5;
    const sections = [
      "1. Purpose: The Partner will use the Platform to manage food court operations.",
      "2. Platform Usage: Menu management, order management, customer interaction, and analytics.",
      "3. Charges: For orders below ₹100: ₹0.50/order. Above ₹100: ₹1/order.",
      "4. Compliance: FSSAI, GST, and other permits required by law.",
      "5. Termination: 30 days' notice. Immediate on breach.",
      "6. Digital Acceptance: Binding without physical signature."
    ];
    sections.forEach(s => { let l = doc.splitTextToSize(s, maxLineWidth); doc.text(l, margin, yPos); yPos += l.length * 6 + 5; });
    doc.save('Sermyble-Partnership-Agreement.pdf');
  };

  const navigation = useNavigate();
  const handleSubmit = async (e: { preventDefault: () => void; }) => {
    e.preventDefault();
    // For outlets: all compliance docs are required
    // For food courts (parent containers): only PAN and GST are required, FSSAI and bank are optional
    if (formData.gstin && !validateGSTNumber(formData.gstin)) { toast.error('Invalid GST Number'); return; }
    if (isOutlet) {
      if (!formData.gstin) { toast.error('GST Number is required for outlets'); return; }
      if (!validateFSSAINumber(formData.fssaiNumber)) { toast.error('Invalid FSSAI Number'); return; }
      if (!uploadedFiles.fssaiDocument) { toast.error('Please upload the FSSAI document'); return; }
      if (!uploadedFiles.bankDocumentFile) { toast.error('Please upload the bank document'); return; }
      if (!formData.accountNumber) { toast.error('Bank account number is required for outlets'); return; }
    } else {
      // For parent food courts, validate FSSAI only if provided
      if (formData.fssaiNumber && !validateFSSAINumber(formData.fssaiNumber)) { toast.error('Invalid FSSAI Number'); return; }
    }
    if (!uploadedFiles.panCardImage) { toast.error('Please upload the PAN card image'); return; }
    if (!uploadedFiles.gstImage && isOutlet) { toast.error('Please upload the GST document'); return; }
    if (!isChecked) { toast.warning("Please accept Terms and Conditions"); return; }

    const data = new FormData();
    data.append("name", formData.foodCourtName);
    data.append("address", formData.address);
    data.append("city", formData.city);
    data.append("latitude", formData.latitude);
    data.append("longitude", formData.longitude);
    data.append("contactNumber", formData.ownerMobile);
    data.append("email", formData.ownerEmail);
    data.append("status", formData.status);
    data.append("menuQrCode", formData.menuQrCode);
    data.append("imageUrl", formData.imageUrl);
    data.append("panNumber", formData.panNumber);
    data.append("panName", formData.panName);
    if (formData.gstin) data.append("gstin", formData.gstin);
    // FSSAI and bank details — always sent for outlets, optional for parent food courts
    if (formData.fssaiNumber) data.append("fssaiNumber", formData.fssaiNumber);
    if (formData.fssaiExpiryDate) data.append("fssaiExpiryDate", formData.fssaiExpiryDate);
    if (formData.accountName) data.append("beneficiaryName", formData.accountName);
    if (formData.accountNumber) data.append("accountNumber", formData.accountNumber);
    if (formData.ifsc) data.append("ifscCode", formData.ifsc);
    if (formData.bankBranch) data.append("bankBranch", formData.bankBranch);
    if (isOutlet && formData.parentFoodCourtId) data.append("parentFoodCourtId", formData.parentFoodCourtId);
    if (uploadedFiles.panCardImage) data.append("panDocumentFile", uploadedFiles.panCardImage);
    if (uploadedFiles.fssaiDocument) data.append("fssaiDocumentFile", uploadedFiles.fssaiDocument);
    if (uploadedFiles.gstImage) data.append("gstDocumentFile", uploadedFiles.gstImage);
    if (uploadedFiles.bankDocumentFile) data.append("bankDocumentFile", uploadedFiles.bankDocumentFile);

    try {
      setLoader(true);
      const res = await registerFoodcourt(data);
      if (res) { setLoader(false); localStorage.removeItem(STORAGE_KEY); generateAndDownloadPDF(); toast.success(res.message); navigation("/success"); }
    } catch { setLoader(false); } finally { setLoader(false); }
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <div className="registration-page">

      {/* ═══ TOP BAR ═══ */}
      <div className="reg-topbar">
        <h1>Register your <span>Food Court</span></h1>
        <p>{isAuthenticated ? 'Complete 3 simple steps and start receiving orders today' : 'Join 500+ food courts growing with Sermyble'}</p>
      </div>

      {/* ═══ AUTH GATE ═══ */}
      {!isAuthenticated ? (
        <div className="auth-gate">

          {/* ── Left: Auth Card ── */}
          <div className="auth-card">
            <div className="auth-card-header">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              <h2>Start your Food Court Journey</h2>
              <p>Sign up with Google or your mobile number to begin registration</p>
            </div>

            {/* Google */}
            <div className="auth-google-wrap">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Google Sign-In failed')}
                text="continue_with"
                shape="pill"
                size="large"
                width="340"
              />
            </div>

            <div className="auth-divider">
              <span>or continue with mobile</span>
            </div>

            {/* Mobile OTP */}
            <div className="auth-otp-section">
              <div className="auth-mobile-row">
                <div className="auth-flag-prefix">
                  <img src={india_flag} alt="IN" style={{ width: 20 }} />
                  <span>+91</span>
                </div>
                <input
                  type="tel"
                  className="auth-mobile-input"
                  placeholder="Enter mobile number"
                  maxLength={10}
                  value={authMobile}
                  disabled={otpSent}
                  onChange={(e) => setAuthMobile(e.target.value.replace(/\D/g, ''))}
                />
                <button
                  className={`auth-send-btn ${otpSent ? 'resend' : ''}`}
                  onClick={sendAuthOtp}
                  disabled={authMobile.length !== 10}
                >
                  {otpSent ? 'Resend' : 'Send OTP'}
                </button>
              </div>

              {otpSent && (
                <div className="auth-otp-row">
                  <input
                    type="text"
                    className="auth-otp-input"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    value={authOtp}
                    onChange={(e) => setAuthOtp(e.target.value.replace(/\D/g, ''))}
                  />
                  <button
                    className="auth-verify-btn"
                    onClick={verifyAuthOtp}
                    disabled={authOtp.length !== 6}
                  >
                    Verify & Continue
                  </button>
                </div>
              )}
            </div>

            <p className="auth-footer-text">By continuing, you agree to Sermyble's <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a></p>

            {/* Trust Badges */}
            <div className="auth-trust-badges">
              <div className="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span>SSL Secure</span>
              </div>
              <div className="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F6EF7" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <span>24-48hr Approval</span>
              </div>
              <div className="trust-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                <span>Zero Setup Fee</span>
              </div>
            </div>
          </div>

          {/* ── Right: Benefits Panel ── */}
          <div className="benefits-panel">
            <h3 className="bp-title">Why Sermyble?</h3>
            <p className="bp-subtitle">Everything you need to manage and grow your food business</p>

            <div className="bp-features">
              <div className="bp-feature">
                <div className="bp-icon" style={{ background: 'rgba(79, 110, 247, 0.15)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F6EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                </div>
                <div className="bp-text">
                  <strong>Digital Menu & QR Ordering</strong>
                  <span>Customers scan, order & pay — zero wait time</span>
                </div>
              </div>

              <div className="bp-feature">
                <div className="bp-icon" style={{ background: 'rgba(34, 197, 94, 0.15)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>
                </div>
                <div className="bp-text">
                  <strong>Real-time Analytics</strong>
                  <span>Track revenue, orders & customer insights live</span>
                </div>
              </div>

              <div className="bp-feature">
                <div className="bp-icon" style={{ background: 'rgba(245, 158, 11, 0.15)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <div className="bp-text">
                  <strong>Multi-Counter Management</strong>
                  <span>Manage multiple stalls from a single dashboard</span>
                </div>
              </div>

              <div className="bp-feature">
                <div className="bp-icon" style={{ background: 'rgba(168, 85, 247, 0.15)' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                </div>
                <div className="bp-text">
                  <strong>Instant Payouts</strong>
                  <span>Direct bank settlement with transparent billing</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="bp-stats">
              <div className="bp-stat">
                <span className="bp-stat-num">500+</span>
                <span className="bp-stat-label">Active Partners</span>
              </div>
              <div className="bp-stat-divider" />
              <div className="bp-stat">
                <span className="bp-stat-num">50K+</span>
                <span className="bp-stat-label">Daily Orders</span>
              </div>
              <div className="bp-stat-divider" />
              <div className="bp-stat">
                <span className="bp-stat-num">4.8★</span>
                <span className="bp-stat-label">Partner Rating</span>
              </div>
            </div>

            {/* Testimonial */}
            <div className="bp-testimonial">
              <p>"Sermyble transformed our food court operations. Orders are faster, staff is happier, and revenue grew 40% in 3 months."</p>
              <div className="bp-testimonial-author">
                <div className="bp-author-avatar">RS</div>
                <div>
                  <strong>Rajesh Sharma</strong>
                  <span>Spice Garden Food Court, Hyderabad</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      ) : (

      /* ═══ MAIN CONTENT (only after auth) ═══ */
      <div className="reg-content">

        {/* ── LEFT: Form ── */}
        <div className="reg-form-col">
          <div className="reg-card">

            {/* Authenticated user badge */}
            <div className="auth-user-strip">
              {googleUser?.picture && <img src={googleUser.picture} alt="" className="auth-user-avatar" />}
              <div className="auth-user-info">
                <span className="auth-user-name">{googleUser?.name || formData.ownerMobile}</span>
                <span className="auth-user-detail">{googleUser?.email || `+91 ${formData.ownerMobile}`}</span>
              </div>
              <div className="auth-user-verified">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <span>Verified</span>
              </div>
            </div>

            {/* Existing locations banner for returning owners */}
            {existingLocations.length > 0 && (
              <div className="existing-locations-banner">
                <div className="elb-header">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <span>You already have {existingLocations.length} registered location{existingLocations.length > 1 ? 's' : ''}:</span>
                </div>
                <div className="elb-list">
                  {existingLocations.map((loc) => (
                    <div key={loc.id} className="elb-item">
                      <span className="elb-name">{loc.name}</span>
                      <span className={`elb-status ${loc.status.toLowerCase().replace(/\s+/g, '-')}`}>{loc.status}</span>
                    </div>
                  ))}
                </div>
                <p className="elb-note">You're registering an additional food court below.</p>
              </div>
            )}            {/* Step tabs */}
            <div className="reg-stepper">
              {steps.map((label, i) => (
                <div key={i} className={`reg-step-tab ${i === activeStep ? 'active' : ''} ${i < activeStep ? 'completed' : ''}`}>
                  <span className="reg-step-num">{i < activeStep ? '✓' : i + 1}</span>
                  {label}
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="reg-progress-bar">
              <div className="reg-progress-fill" style={{ width: `${Math.round(((activeStep + 1) / steps.length) * 100)}%` }} />
            </div>
            <p className="reg-progress-label">Step {activeStep + 1} of {steps.length} • {Math.round(((activeStep + 1) / steps.length) * 100)}% complete</p>

            {/* ═══ STEP 1 ═══ */}
            {activeStep === 0 && (
              <>
                <div className="form-section-title">
                  <div className="form-section-icon blue"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
                  <h6>What are you registering?</h6>
                </div>
                <div className="type-cards">
                  <div className={`type-card ${!isOutlet ? 'selected' : ''}`} onClick={() => setIsOutlet(false)}>
                    <span className="type-card-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="6" x2="15" y2="6"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="15" y2="18"/></svg></span>
                    <span className="type-card-label">Food Court</span>
                    <span className="type-card-desc">Register a new food court</span>
                  </div>
                  <div className={`type-card ${isOutlet ? 'selected' : ''}`} onClick={() => setIsOutlet(true)}>
                    <span className="type-card-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg></span>
                    <span className="type-card-label">Outlet</span>
                    <span className="type-card-desc">Add outlet to existing food court</span>
                  </div>
                </div>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField fullWidth label={isOutlet ? "Outlet Name" : "Food Court Name"} id="foodCourtName" required value={formData.foodCourtName} onChange={handleInputChange} size="small" />
                  </Grid>
                  {isOutlet && (
                    <Grid item xs={12}>
                      <TextField fullWidth label="Parent Food Court ID" id="parentFoodCourtId" required={isOutlet} value={formData.parentFoodCourtId} onChange={handleInputChange} size="small" helperText="Enter the UUID of the parent Food Court" />
                    </Grid>
                  )}
                </Grid>

                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Referral Code (Optional)" id="referralCode" value={formData.referralCode} onChange={handleInputChange} size="small" helperText="Referred by an existing partner? Enter their code" />
                  </Grid>
                </Grid>

                <div className="form-section-title">
                  <div className="form-section-icon amber"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>
                  <h6>Owner Information</h6>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Owner Name" id="ownerName" required value={formData.ownerName} onChange={handleInputChange} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Email Address" id="ownerEmail" type="email" required value={formData.ownerEmail} onChange={handleInputChange} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Mobile Number" id="ownerMobile" required value={formData.ownerMobile} onChange={handleInputChange} size="small" inputProps={{ maxLength: 10 }}
                      InputProps={{ startAdornment: (<InputAdornment position="start"><img src={india_flag} alt="IN" style={{ width: 18 }} /><span style={{ marginLeft: 3, fontSize: 12, color: '#374151' }}>+91</span></InputAdornment>) }}
                      disabled={isOtpVerified}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <div className="otp-verified-badge">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                      <span>{isOtpVerified ? 'Mobile Verified' : 'Verified via Google'}</span>
                    </div>
                  </Grid>
                </Grid>

                <div className="form-section-title">
                  <div className="form-section-icon cyan"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div>
                  <h6>Location</h6>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                      <TextField fullWidth id="autocomplete" label="Search your location" placeholder="Type address..." size="small" />
                    </Autocomplete>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Full Address" id="address" required value={formData.address} onChange={handleInputChange} InputProps={{ readOnly: true }} size="small" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="State" value={stateValue || "-"} InputProps={{ readOnly: true }} size="small" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label="Country" value="India" InputProps={{ readOnly: true }} size="small" />
                  </Grid>
                  <Grid item xs={12}>
                    <Box className="map-box">
                      <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} zoom={10} center={markerPosition} onClick={handleMapClick}>
                        <Marker position={markerPosition} draggable={true} onDragEnd={handleMarkerDragEnd} />
                      </GoogleMap>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}

            {/* ═══ STEP 2 ═══ */}
            {activeStep === 1 && (
              <>
                {!isOutlet && (
                  <div className="form-section-note" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1E40AF', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>As a food court, only PAN is required. FSSAI & bank details are optional — each outlet will provide their own.</span>
                  </div>
                )}
                <div className="form-section-title"><div className="form-section-icon amber"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg></div><h6>PAN Card Details <Tooltip text="Your 10-digit PAN number (e.g. ABCDE1234F). Found on your PAN card issued by Income Tax Department." /></h6></div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="PAN Number" id="panNumber" required value={formData.panNumber} onChange={handleInputChange} inputProps={{ maxLength: 10 }} size="small"
                      error={validationErrors.panNumber !== undefined && validationErrors.panNumber !== ''}
                      helperText={formData.panNumber ? (validationErrors.panNumber || validators.panNumber.okMsg) : ''}
                      FormHelperTextProps={{ style: { color: validationErrors.panNumber ? '#DC2626' : '#16A34A' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Name on PAN" id="panName" required value={formData.panName} onChange={handleInputChange} size="small" /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Address (Optional)" id="address" value={formData.address} onChange={handleInputChange} size="small" /></Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography className="file-upload-lbl">PAN Card Image *</Typography>
                    <FileDropZone id="panCardImage" label="Upload PAN Card" file={uploadedFiles.panCardImage} />
                  </Grid>
                </Grid>

                <div className="form-section-title"><div className="form-section-icon pink"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg></div><h6>GST Registration {!isOutlet && <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>(Optional)</span>} <Tooltip text="Your 15-digit GSTIN (e.g. 22AAAAA0000A1Z5). Found on your GST Registration Certificate." /></h6></div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="GSTIN Number" id="gstin" required value={formData.gstin} onChange={handleInputChange} inputProps={{ maxLength: 15 }} size="small"
                      error={validationErrors.gstin !== undefined && validationErrors.gstin !== ''}
                      helperText={formData.gstin ? (validationErrors.gstin || validators.gstin.okMsg) : ''}
                      FormHelperTextProps={{ style: { color: validationErrors.gstin ? '#DC2626' : '#16A34A' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography className="file-upload-lbl">GST Certificate *</Typography>
                    <FileDropZone id="gstImage" label="Upload GST Certificate" file={uploadedFiles.gstImage} />
                  </Grid>
                </Grid>

                <div className="form-section-title"><div className="form-section-icon green"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div><h6>FSSAI License {!isOutlet && <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>(Optional)</span>} <Tooltip text="Your 14-digit FSSAI number. Found on your Food License certificate issued by FSSAI." /></h6></div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth label="FSSAI Number" id="fssaiNumber" required value={formData.fssaiNumber} onChange={handleInputChange} inputProps={{ maxLength: 14 }} size="small"
                      error={validationErrors.fssaiNumber !== undefined && validationErrors.fssaiNumber !== ''}
                      helperText={formData.fssaiNumber ? (validationErrors.fssaiNumber || validators.fssaiNumber.okMsg) : ''}
                      FormHelperTextProps={{ style: { color: validationErrors.fssaiNumber ? '#DC2626' : '#16A34A' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Expiry Date" id="fssaiExpiryDate" type="date" required value={formData.fssaiExpiryDate} onChange={handleInputChange} InputLabelProps={{ shrink: true }} size="small" /></Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography className="file-upload-lbl">FSSAI Document *</Typography>
                    <FileDropZone id="fssaiDocument" label="Upload FSSAI License" file={uploadedFiles.fssaiDocument} />
                  </Grid>
                </Grid>

                <div className="form-section-title"><div className="form-section-icon blue"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div><h6>Bank Account {!isOutlet && <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}>(Optional)</span>}</h6></div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Account Number" id="accountNumber" required value={formData.accountNumber} onChange={handleInputChange} inputProps={{ maxLength: 12 }} size="small" /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Account Holder Name" id="accountName" required value={formData.accountName} onChange={handleInputChange} size="small" /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="IFSC Code" id="ifsc" required value={formData.ifsc} onChange={handleInputChange} inputProps={{ maxLength: 11 }} size="small" /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Bank & Branch" id="bankBranch" required value={formData.bankBranch} onChange={handleInputChange} size="small" /></Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography className="file-upload-lbl">Bank Proof *</Typography>
                    <FileDropZone id="bankDocumentFile" label="Upload Bank Proof" file={uploadedFiles.bankDocumentFile} />
                  </Grid>
                </Grid>
              </>
            )}

            {/* ═══ STEP 3 ═══ */}
            {activeStep === 2 && (
              <>
                <div className="form-section-title"><div className="form-section-icon green"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></div><h6>Partnership Agreement</h6></div>
                <div className="agreement-content">
                  <p><strong>Effective Date</strong>: {new Date().toLocaleDateString()}</p>
                  <p>This Agreement is between <strong>Sermyble</strong> and <strong>{formData.foodCourtName || '[Food Court]'}</strong>, represented by <strong>{formData.ownerName || '[Owner]'}</strong>.</p>
                  <h4>1. Purpose</h4><p>Terms for using the Platform to manage food court operations.</p>
                  <h4>2. Services</h4><p>Digital tools for order management, analytics, and payment processing.</p>
                  <h4>3. Obligations</h4><p>Maintain accurate info, comply with food safety regulations, quality service.</p>
                  <h4>4. Revenue</h4><p>Terms in separate commercial agreement. Transparent reporting provided.</p>
                  <h4>5. Privacy</h4><p>Responsible data handling per applicable laws.</p>
                  <h4>6. Termination</h4><p>30 days notice by either party.</p>
                </div>
                <div className="agreement-check">
                  <input type="checkbox" id="terms-check" checked={isChecked} onChange={handleCheckboxChange} />
                  <label htmlFor="terms-check">I accept the partnership terms and conditions</label>
                </div>
              </>
            )}

            {/* Buttons */}
            <div className="btn-row">
              {activeStep > 0 && (
                <Button variant="outlined" onClick={handleBack} className="btn-prev">Back</Button>
              )}
              <Button fullWidth variant="contained" onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext} disabled={Loader} className="btn-next">
                {activeStep === steps.length - 1 ? (Loader ? 'Submitting...' : 'Submit Registration') : 'Continue →'}
              </Button>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Sidebar ── */}
        <div className="reg-sidebar">

          {/* Progress */}
          <div className="sidebar-card">
            <h4><span className="sc-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg></span> Your Progress</h4>
            <ul className="progress-list">
              <li className={`progress-item ${activeStep > 0 ? 'done' : activeStep === 0 ? 'current' : ''}`}>
                <span className="progress-dot">{activeStep > 0 ? '✓' : '1'}</span>
                Establishment Details
              </li>
              <li className={`progress-item ${activeStep > 1 ? 'done' : activeStep === 1 ? 'current' : ''}`}>
                <span className="progress-dot">{activeStep > 1 ? '✓' : '2'}</span>
                Compliance Documents
              </li>
              <li className={`progress-item ${activeStep > 2 ? 'done' : activeStep === 2 ? 'current' : ''}`}>
                <span className="progress-dot">{activeStep > 2 ? '✓' : '3'}</span>
                Partnership Agreement
              </li>
            </ul>
          </div>

          {/* Documents needed */}
          <div className="sidebar-card">
            <h4><span className="sc-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></span> Documents Needed</h4>
            <ul className="doc-list">
              <li><span className="doc-emoji"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg></span> PAN Card</li>
              <li><span className="doc-emoji"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg></span> GST Certificate</li>
              <li><span className="doc-emoji"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></span> FSSAI License</li>
              <li><span className="doc-emoji"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></span> Bank Proof (cheque / passbook)</li>
            </ul>
          </div>

          {/* Help */}
          <div className="sidebar-card">
            <h4><span className="sc-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span> Need Help?</h4>
            <p className="help-text">
              Having trouble with registration? Contact us at <a href="mailto:info@aslinformationservices.com">info@aslinformationservices.com</a> or call <strong>+91 70932 81075</strong>
            </p>
          </div>
        </div>

      </div>
      )}

      {showTimeoutModal && (
        <div className="timeout-overlay">
          <div className="timeout-modal">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <h3>Session Expiring Soon</h3>
            <p>Your session will expire in 5 minutes due to inactivity. Click below to continue.</p>
            <button className="timeout-btn" onClick={resetTimeout}>I'm Still Here</button>
          </div>
        </div>
      )}

      {/* Draft restore modal */}
      {showDraftModal && (
        <div className="timeout-overlay">
          <div className="timeout-modal">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4F6EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            <h3>Previous Draft Found</h3>
            <p>You have an unsaved registration for <strong>{pendingDraft?.formData?.foodCourtName || 'a food court'}</strong>. Would you like to continue or start fresh?</p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="timeout-btn" onClick={handleRestoreDraft}>Continue Draft</button>
              <button className="timeout-btn" style={{ background: 'transparent', color: '#374151', border: '1px solid #E5E7EB' }} onClick={handleDiscardDraft}>Start Fresh</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
